import React, {
  useContext,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle
} from "react";

import { ApplicationsContext } from "../../context/applications/ApplicationContext";
import { AuthContext } from "../../context/auth/AuthContext";
import { Country, Province, Canton, District } from "../../models/interfaces";
import Message from "../ui/Message";

interface AddressFormProps {
  title: string;
  addressType: string;
  showPostalCode?: boolean;
  showMerchantName?: boolean;
  data: any;
  onChange: (data: any) => void;
  readOnly?: boolean;
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const AddressForm = forwardRef(
  (
    {
      title,
      addressType,
      showPostalCode = false,
      showMerchantName = false,
      data,
      onChange,
      readOnly = false
    }: AddressFormProps,
    ref
  ) => {
    const { loadCountries, loadProvinces, loadCantons, loadDistricts, getZipCode } =
      useContext(ApplicationsContext);

    const { user } = useContext(AuthContext);

    const [countries, setCountries] = useState<Country[]>([]);
    const [provinces, setProvinces] = useState<Province[]>([]);
    const [cantons, setCantons] = useState<Canton[]>([]);
    const [districts, setDistricts] = useState<District[]>([]);

    const [errorMessage, setErrorMessage] = useState("");
    const [emailError, setEmailError] = useState("");
    const [urlError, setUrlError] = useState("");

    const isRegistration = addressType === "REGISTRATION_ADDRESS";

    const emailErrorText = isRegistration
      ? "Correo(s) inválido(s). Separe varios correos con ;"
      : "Correo inválido";

    // =========================
    // VALIDAR CORREO(S)
    // =========================
    const validateEmails = (value: string): boolean => {
      if (!value || !value.trim()) return false;

      // REGISTRATION_ADDRESS permite varios correos separados por ;
      if (isRegistration) {
        const emails = value
          .split(";")
          .map((e) => e.trim())
          .filter((e) => e !== ""); // tolera ";" al final
        return emails.length > 0 && emails.every((e) => emailRegex.test(e));
      }

      return emailRegex.test(value.trim());
    };

    // =========================
    // SINCRONIZAR CONTACTO
    // =========================
    /*useEffect(() => {
      if (!readOnly && !data.firstName) {
        onChange({
          ...data,
          firstName: data.contact.firstName,
          lastName: data.contact.lastName,
          mobilePhone: data.contact.mobilePhone,
          phone: data.contact.phone,
          email: data.contact.email
        });
      }
    }, [data.contact?.firstName]);*/

    // =========================
    // CARGAR PAISES
    // =========================
    useEffect(() => {
      loadCountries().then(setCountries);
    }, []);

    // =========================
    // PAIS DEFAULT USUARIO
    // =========================
    useEffect(() => {
      if (!data.country && user?.country?.id && !readOnly) {
        onChange({
          ...data,
          country: user.country.id
        });
      }
    }, [data.country, user?.country?.id]);

    // =========================
    // PROVINCIAS
    // =========================
    useEffect(() => {
      if (!data.country) {
        setProvinces([]);
        return;
      }
      loadProvinces(data.country).then(setProvinces);
    }, [data.country]);

    // =========================
    // CANTONES
    // =========================
    useEffect(() => {
      if (!data.country || !data.provinceId) {
        setCantons([]);
        return;
      }
      loadCantons(data.country, Number(data.provinceId)).then(setCantons);
    }, [data.country, data.provinceId]);

    useEffect(() => {
      if (!cantons.length) return;
      if (data.cantonCode) return;
      if (!data.cantonName) return;

      const match = cantons.find(
        (c) =>
          c.cantonName.toUpperCase().trim() ===
          data.cantonName.toUpperCase().trim()
      );

      if (!match) return;

      onChange({
        ...data,
        cantonCode: String(match.cantonCode),
        cantonName: match.cantonName
      });
    }, [cantons]);

    // =========================
    // DISTRITOS
    // =========================
    useEffect(() => {
      if (!data.country || !data.provinceId || !data.cantonCode) {
        setDistricts([]);
        return;
      }
      loadDistricts(
        data.country,
        Number(data.provinceId),
        Number(data.cantonCode)
      ).then(setDistricts);
    }, [data.country, data.provinceId, data.cantonCode]);

    useEffect(() => {
      if (!districts.length) return;
      if (data.districtCode) return;
      if (!data.districtName) return;

      const match = districts.find(
        (d) =>
          d.districtName.toUpperCase().trim() ===
          data.districtName.toUpperCase().trim()
      );

      if (!match) return;

      onChange({
        ...data,
        districtCode: String(match.districtCode),
        districtName: match.districtName
      });
    }, [districts]);

    // =========================
    // HANDLERS
    // =========================
    const handleChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
      if (readOnly) return;

      const { name, value } = e.target;

      if (name === "country") {
        onChange({
          ...data,
          country: Number(value),
          provinceId: "",
          cantonCode: "",
          districtCode: ""
        });
      } else if (name === "districtCode") {
        const selected = districts.find(
          (d) => String(d.districtCode) === value
        );
        onChange({
          ...data,
          districtCode: value,
          districtName: selected?.districtName || ""
        });

        if (user?.country?.code === "CRI" && value) {
          getZipCode(
            Number(data.country),
            Number(data.provinceId),
            Number(data.cantonCode),
            Number(value)
          ).then((zip) => {
            onChange({
              ...data,
              districtCode: value,
              districtName: selected?.districtName || "",
              postalCode: zip,
            });
          });
        }

        if (user?.country?.code === "PAN" && user?.banks[0].code == '0054' && value) {
          onChange({
            ...data,
            districtCode: value,
            districtName: selected?.districtName || "",
            postalCode: '0000',
          });
        }
      } else {
        onChange({
          ...data,
          [name]: value
        });
      }
    };

    const handleProvinceChange = (
      e: React.ChangeEvent<HTMLSelectElement>
    ) => {
      if (readOnly) return;
      const value = e.target.value;
      const selected = provinces.find(
        (p) => String(p.provinceId) === value
      );
      onChange({
        ...data,
        provinceId: value,
        provinceName: selected?.provinceName || "",
        cantonCode: "",
        districtCode: ""
      });
    };

    const handleCantonChange = (
      e: React.ChangeEvent<HTMLSelectElement>
    ) => {
      if (readOnly) return;
      const value = e.target.value;
      const selected = cantons.find(
        (c) => String(c.cantonCode) === value
      );
      onChange({
        ...data,
        cantonCode: value,
        cantonName: selected?.cantonName || "",
        districtCode: ""
      });
    };

    const handleEmailChange = (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
      const value = e.target.value;

      if (!validateEmails(value)) {
        setEmailError(emailErrorText);
      } else {
        setEmailError("");
      }

      onChange({
        ...data,
        email: value
      });
    };

    const handleUrlChange = (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
      const value = e.target.value;

      const urlRegex = /^(https?:\/\/)?([\w\d-]+\.)+\w{2,}(\/.*)?$/;

      if (value && !urlRegex.test(value)) {
        setUrlError("URL inválida");
      } else {
        setUrlError("");
      }

      onChange({
        ...data,
        url: value
      });
    };

    const removeError = () => setErrorMessage("");

    // =========================
    // VALIDACION EXTERNA
    // =========================
    useImperativeHandle(ref, () => ({
      validate: () => {
        if (readOnly) return true;

        let requiredFields = [
          "provinceId",
          "cantonCode",
          "districtCode",
          "postalCode",
          "firstName",
          "lastName",
          "phone",
          "mobilePhone",
          "email"
        ];

        if (addressType == "PAYMENT_SCHEME_ADDRESS") {
          requiredFields.push("location");
          requiredFields.push("merchantName");
        } else {
          requiredFields.push("addressLine1");
        }

        for (const field of requiredFields) {
          const value = data[field];
          if (
            value === undefined ||
            value === null ||
            value.toString().trim() === ""
          ) {
            setErrorMessage(
              "Por favor complete todos los campos requeridos."
            );
            return false;
          }
        }

        if (!validateEmails(data.email || "")) {
          setEmailError(emailErrorText);
          setErrorMessage("Por favor ingrese un correo válido.");
          return false;
        }

        if (emailError) {
          setErrorMessage("Por favor ingrese un correo válido.");
          return false;
        }

        setErrorMessage("");
        return true;
      }
    }));

    // =========================
    // RENDER
    // =========================
    return (
      <div className="mb-4">
        <h6 className="mb-3">{title}</h6>
        <div className="row">
          {/* Tipo dirección */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Tipo dirección</label>
            <input
              className="form-control"
              value={
                addressType === "REGISTRATION_ADDRESS"
                  ? "Registration Address"
                  : "Address for Payment Scheme"
              }
              disabled
            />
          </div>

          {/* País */}
          <div className="col-md-6 mb-3">
            <label className="form-label">País *</label>
            <select
              name="country"
              className="form-select"
              value={data.country || ""}
              disabled
            >
              <option value="">Seleccione</option>
              {countries.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Provincia */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Provincia *</label>
            <select
              className="form-select"
              value={data.provinceId || ""}
              onChange={handleProvinceChange}
              disabled={readOnly || !provinces.length}
            >
              <option value="">Seleccione</option>
              {provinces.map((p) => (
                <option key={p.provinceId} value={p.provinceId}>
                  {p.provinceName}
                </option>
              ))}
            </select>
          </div>

          {/* Cantón */}
          <div className="col-md-6 mb-3">
            <label className="form-label">
              {user.country.code === "PAN" ? "Distrito *" : "Cantón *"}
            </label>
            <select
              className="form-select"
              value={data.cantonCode || ""}
              onChange={handleCantonChange}
              disabled={readOnly || !cantons.length}
            >
              <option value="">Seleccione</option>
              {cantons.map((c) => (
                <option key={c.cantonCode} value={c.cantonCode}>
                  {c.cantonName}
                </option>
              ))}
            </select>
          </div>

          {/* Distrito */}
          <div className="col-md-6 mb-3">
            <label className="form-label">
              {user.country.code === "PAN"
                ? "Corregimiento *"
                : "Distrito *"}
            </label>
            <select
              name="districtCode"
              className="form-select"
              value={data.districtCode || ""}
              onChange={handleChange}
              disabled={readOnly || !districts.length}
            >
              <option value="">Seleccione</option>
              {districts.map((d) => (
                <option key={d.districtCode} value={d.districtCode}>
                  {d.districtName}
                </option>
              ))}
            </select>
          </div>

          {/* Código postal */}
          <div className="col-6 mb-3">
            <label className="form-label">Código Postal *</label>
            <input
              name="postalCode"
              className="form-control"
              value={data.postalCode || ""}
              onChange={handleChange}
              disabled={readOnly || user?.country?.code === "CRI"}
            />
          </div>

          {/* Dirección principal / Nombre Comercio */}
          {addressType === "REGISTRATION_ADDRESS" ? (
            <div className="col-12 mb-3">
              <label className="form-label">Dirección principal *</label>
              <input
                name="addressLine1"
                className="form-control"
                value={data.addressLine1 || ""}
                onChange={handleChange}
                disabled={readOnly}
                maxLength={40}
              />
            </div>
          ) : (
            <div className="col-12 mb-3">
              <label className="form-label">Nombre Comercio *</label>
              <input
                name="merchantName"
                className="form-control"
                value={data.merchantName || ""}
                onChange={handleChange}
                disabled={readOnly}
                maxLength={38}
              />
            </div>
          )}

          {/* Dirección secundaria / Ubicación */}
          {addressType === "REGISTRATION_ADDRESS" ? (
            <div className="col-12 mb-3">
              <label className="form-label">Dirección secundaria</label>
              <input
                name="addressLine2"
                className="form-control"
                value={data.addressLine2 || ""}
                onChange={handleChange}
                disabled={readOnly}
                maxLength={40}
              />
            </div>
          ) : (
            <div className="col-12 mb-3">
              <label className="form-label">Ubicación Exacta *</label>
              <input
                name="location"
                className="form-control"
                value={data.location || ""}
                onChange={handleChange}
                disabled={readOnly}
                maxLength={32}
              />
            </div>
          )}

          {/* Nombre y Apellido */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Nombre *</label>
            <input
              name="firstName"
              className="form-control"
              value={data.firstName || ""}
              onChange={handleChange}
              disabled={readOnly}
            />
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">Apellido *</label>
            <input
              name="lastName"
              className="form-control"
              value={data.lastName || ""}
              onChange={handleChange}
              disabled={readOnly}
            />
          </div>

          {/* Teléfono y Celular */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Teléfono *</label>
            <input
              name="phone"
              className="form-control"
              value={data.phone || ""}
              onChange={handleChange}
              disabled={readOnly}
            />
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">Celular *</label>
            <input
              name="mobilePhone"
              className="form-control"
              value={data.mobilePhone || ""}
              onChange={handleChange}
              disabled={readOnly}
            />
          </div>

          {/* Correo */}
          <div className="col-md-6 mb-3">
            <label className="form-label">
              {isRegistration ? "Correo(s) *" : "Correo *"}
            </label>
            <input
              name="email"
              type={isRegistration ? "text" : "email"}
              className={`form-control ${emailError ? "is-invalid" : ""}`}
              value={data.email || ""}
              onChange={handleEmailChange}
              disabled={readOnly}
              placeholder={
                isRegistration ? "correo1@dominio.com;correo2@dominio.com" : ""
              }
            />
            {emailError && (
              <div className="invalid-feedback">{emailError}</div>
            )}
          </div>

          {/* URL */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Página web</label>
            <input
              name="url"
              type="url"
              className={`form-control ${urlError ? "is-invalid" : ""}`}
              value={data.url || ""}
              onChange={handleUrlChange}
              disabled={readOnly}
            />
            {urlError && (
              <div className="invalid-feedback">{urlError}</div>
            )}
          </div>

          {/* Mensaje de error */}
          {errorMessage && (
            <Message
              type="error"
              message={errorMessage}
              closeDialog={removeError}
              handleOk={removeError}
              backgroundColor="rgba(0,0,0,0.4)"
            />
          )}
        </div>
      </div>
    );
  }
);